import {
    Component,
    ElementRef,
    Input, OnChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { AbstractComponent } from '../../../definition/abstract/abstract.component';
import { ZipSearcherService } from '../../../service/zip-searcher.service';
import { ZipModel } from '../../../service/model/zip.model';
import { AddressModel } from './model/address.model';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';

@Component({
    selector: 'app-address-selector',
    templateUrl: './address-selector.component.html',
    styleUrls: ['./address-selector.component.scss']
})
export class AddressSelectorComponent extends AbstractComponent {
    @Input() address: AddressModel;
    @Input() formGroup: FormGroup;
    @Input() readonly: boolean = false;

    @ViewChild('trigger') trigger!: ElementRef;
    @ViewChild('addressTemplate') addressTemplate!: TemplateRef<any>;

    overlayRef!: OverlayRef;
    touched = false;

    constructor(
        protected zipService: ZipSearcherService,
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef
    ) {
        super();
    }

    public override ngOnInit(): void {
        if (this.formGroup) {
            if (this.formGroup.valid) {
                this.patchAddressWithFormsValue(this.formGroup.getRawValue())
            }
            if (this.address) {
                this.patchFormWithAddress();
            }
            this.watchFormsChanges();
        }
    }

    protected enableFields(): void {
        this.formGroup.get('city')?.enable({ emitEvent: false });
        this.formGroup.get('streetName')?.enable({ emitEvent: false });
        this.formGroup.get('state')?.enable({ emitEvent: false });
        this.formGroup.get('neighborhood')?.enable({ emitEvent: false });
    }
    public eventOpenAddress(): void {
        if (this.overlayRef) {
            this.eventCloseOverlay();
        }
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.trigger)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 8,
                }
            ]);

        const triggerWidth = this.trigger.nativeElement.getBoundingClientRect().width;
        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            width: triggerWidth,
            minWidth: triggerWidth,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });

        this.overlayRef.backdropClick().subscribe(() => { this.eventCloseOverlay(); });

        const menuTemplateBuilt = new TemplatePortal(this.addressTemplate, this.viewContainerRef);

        this.overlayRef.attach(menuTemplateBuilt);
    }
    protected eventCloseOverlay(): void {
        this.overlayRef.dispose();
        this.touched = true;
    }
    protected eventZipCodeChanged(event: Event): void {
        const zipCode = (event.target as HTMLInputElement).value.replace('-', '');

        if (zipCode.length !== 8) {
            this.formGroup.get('zipCode')?.setErrors({ zipCode: 'invalid' });
            return;
        }
        this.zipService.getZipCode(zipCode).subscribe({
            next: (zipCode) => {
                if ((zipCode as ZipModel).cep) {
                    this.setAddressInfo(zipCode as ZipModel);
                } else {
                    this.formGroup.get('zipCode')?.setErrors({ zipCode: 'invalid' });
                }
            }, error: err => {
                this.formGroup.get('zipCode')?.setErrors({ zipCode: 'invalid' });
            }
        });

    }
    protected isValidCep(): boolean {
        const zipCodeControl = this.formGroup.get('zipCode') as FormControl;
        return zipCodeControl.value && zipCodeControl.value.length === 8 &&
            !zipCodeControl.hasError('zipCode');
    }
    protected isValidAddress(): boolean {
        return (!this.formGroup && this.isAddressValid()) || this.isCepAndFormValid();
    }
    private isAddressValid(): boolean {
        return !!this.address && !!this.address.documentNumber && !!this.address.zipCode && !!this.address.number && !!this.address.name && !!this.address.state &&
            !!this.address.neighborhood && !!this.address.city && !!this.address.streetName;
    }
    private isCepAndFormValid() {
        return this.formGroup && this.formGroup.valid && this.isValidCep();
    }

    protected setAddressInfo(zipCode: ZipModel): void {
        this.setFormFieldValue('streetName', zipCode.logradouro);
        this.setFormFieldValue('city', zipCode.localidade);
        this.setFormFieldValue('state', zipCode.uf);
        this.setFormFieldValue('neighborhood', zipCode.bairro);
        this.setFormFieldValue('country', 'Brasil');
    }
    protected setFormFieldValue(controlName: string, value: string | number | null | boolean): void {
        (this.formGroup.get(controlName) as FormControl).setValue(value);
        (this.formGroup.get(controlName) as FormControl).enable();
    }
    protected patchAddressWithFormsValue(formRaw: AddressModel): void {
        if (!this.address) {
            this.address = {} as AddressModel;
        }
        this.address.streetName = formRaw.streetName;
        this.address.city = formRaw.city;
        this.address.state = formRaw.state;
        this.address.zipCode = formRaw.zipCode;
        this.address.neighborhood = formRaw.neighborhood;
        this.address.number = formRaw.number;
        this.address.complement = formRaw.complement;
    }
    protected patchFormWithAddress(): void {
        this.formGroup.patchValue(this.address)
    }
    protected watchFormsChanges(): void {
        this.formGroup.valueChanges.subscribe(changes => {
           this.patchAddressWithFormsValue(changes as AddressModel);
           if (this.formGroup.get('zipCode')?.valid) {
                this.enableFields();
           }
        });
    }
}
